import { CreateAddressDTO } from '@reposit/api-client';
import { Formik, FormikProps } from 'formik';
import countries from 'i18n-iso-countries';
import { get } from 'lodash';
import React from 'react';
import { Col, Container, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import * as yup from 'yup';
import PropertyIcon from '../../assets/svg/property.svg';
import Button from '../../components/Button';
import FlashMessage from '../../components/FlashMessage';
import { Input } from '../../components/FormFields';
import FieldWithLabel from '../../components/FormFields/FieldWithLabel';
import Select from '../../components/FormFields/Select';
import RepositCard from '../../components/Reposit/RepositCard';
import { P1 } from '../../components/Typography';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import { UPDATE_ORG_ADDRESS_STORE_KEY, updateOrganizationAddressRequested } from '../../redux/organization/organization.actions';
import { getOrganization } from '../../redux/selectors/organization.selectors';
import { countryList, UK_POSTCODE_REG_EX } from '../../utils/common.utils';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../FlashMessage';

interface OrganizationAddressProps {
  organizationId: string;
}

interface AddressFormValues {
  address: {
    roomNumber: string;
    flatNumber: string;
    buildingNumber: string;
    buildingName: string;
    street: string;
    subStreet: string;
    town: string;
    county: string;
    postcode: string;
    country: string;
  };
}

const OPTIONAL_ADDRESS_KEYS = [
  'roomNumber',
  'flatNumber',
  'buildingNumber',
  'buildingName',
  'subStreet',
  'town',
  'county',
] as const;

const Schema = yup.object().shape({
  address: yup.object().shape({
    buildingNumber: yup.string().test('building', 'Either a building number or name is required', function (value) {
      return !!(value || this.parent.buildingName);
    }),
    buildingName: yup.string().test('building', 'Either a building number or name is required', function (value) {
      return !!(value || this.parent.buildingNumber);
    }),
    street: yup.string().required('Required'),
    postcode: yup
      .string()
      .required('Required')
      .test('uk-postcode', 'Must be a UK postcode', function (value) {
        const country = countries.toAlpha3(this.parent.country) || this.parent.country;
        if (country !== 'GBR') return true;
        return UK_POSTCODE_REG_EX.test(value || '');
      }),
    country: yup.string().required('Required'),
  }),
});

const FormActions = styled.div`
  align-items: center;
  border-top: 1px solid ${(props) => props.theme.colors.dividerDark};
  display: flex;
  justify-content: flex-end;
  margin: 36px 0 0;
  padding: 22px 0 0;
`;

const toCreateAddressDTO = (address: AddressFormValues['address']): CreateAddressDTO => {
  const payload: CreateAddressDTO = {
    street: address.street,
    postcode: address.postcode,
    country: countries.toAlpha3(address.country) || address.country,
  };

  OPTIONAL_ADDRESS_KEYS.forEach((key) => {
    if (address[key]) {
      payload[key] = address[key];
    }
  });

  return payload;
};

const OrganizationAddress: React.FC<OrganizationAddressProps> = ({ organizationId }) => {
  const dispatch = useDispatch();
  const currentOrg = useSelector(getOrganization(organizationId));
  const isLoading = useSelector(createLoadingSelector([UPDATE_ORG_ADDRESS_STORE_KEY]));
  const [flashMessagePayload, dismissFlashMessage] = useFlashMessage([UPDATE_ORG_ADDRESS_STORE_KEY]);
  const storedAddress = get(currentOrg, 'attributes.address');

  return (
    <Container>
      <Row>
        <Col lg={10} push={{ lg: 1 }}>
          <RepositCard
            title="Company address"
            icon={PropertyIcon}
            flashMessage={
              flashMessagePayload ? (
                <FlashMessage
                  payload={flashMessagePayload}
                  onDismiss={dismissFlashMessage}
                  timeRemaining={FLASH_MESSAGE_TIMEOUT}
                />
              ) : undefined
            }
          >
            <P1>This is the organisation's registered company address.</P1>
            <Formik
              enableReinitialize
              initialValues={{
                address: {
                  roomNumber: get(storedAddress, 'roomNumber', ''),
                  flatNumber: get(storedAddress, 'flatNumber', ''),
                  buildingNumber: get(storedAddress, 'buildingNumber', ''),
                  buildingName: get(storedAddress, 'buildingName', ''),
                  street: get(storedAddress, 'street', ''),
                  subStreet: get(storedAddress, 'subStreet', ''),
                  town: get(storedAddress, 'town', ''),
                  county: get(storedAddress, 'county', ''),
                  postcode: get(storedAddress, 'postcode', ''),
                  country: get(storedAddress, 'country', '') || 'GBR',
                },
              }}
              validationSchema={Schema}
              onSubmit={(values) => {
                dispatch(
                  updateOrganizationAddressRequested({
                    organizationId,
                    address: toCreateAddressDTO(values.address),
                  }),
                );
              }}
            >
              {({
                values,
                handleSubmit,
                handleChange,
                handleBlur,
                touched,
                errors,
                setFieldValue,
              }: FormikProps<AddressFormValues>) => {
                return (
                  <form onSubmit={handleSubmit}>
                    <Container fluid style={{ padding: 0 }}>
                      <Row>
                        <Col lg={12}>
                          <FieldWithLabel
                            label="Room Number"
                            touched={get(touched, 'address.roomNumber', false)}
                            error={get(errors, 'address.roomNumber', '')}
                          >
                            <Input
                              name="address.roomNumber"
                              value={values.address.roomNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              touched={get(touched, 'address.roomNumber', false)}
                              error={get(errors, 'address.roomNumber', '')}
                            />
                          </FieldWithLabel>
                          <FieldWithLabel
                            label="Flat Number"
                            touched={get(touched, 'address.flatNumber', false)}
                            error={get(errors, 'address.flatNumber', '')}
                          >
                            <Input
                              name="address.flatNumber"
                              value={values.address.flatNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              touched={get(touched, 'address.flatNumber', false)}
                              error={get(errors, 'address.flatNumber', '')}
                            />
                          </FieldWithLabel>
                          <FieldWithLabel
                            label="Building Number"
                            touched={get(touched, 'address.buildingNumber', false)}
                            error={get(errors, 'address.buildingNumber', '')}
                          >
                            <Input
                              name="address.buildingNumber"
                              value={values.address.buildingNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              touched={get(touched, 'address.buildingNumber', false)}
                              error={get(errors, 'address.buildingNumber', '')}
                            />
                          </FieldWithLabel>
                          <FieldWithLabel
                            label="Building Name"
                            touched={get(touched, 'address.buildingName', false)}
                            error={get(errors, 'address.buildingName', '')}
                          >
                            <Input
                              name="address.buildingName"
                              value={values.address.buildingName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              touched={get(touched, 'address.buildingName', false)}
                              error={get(errors, 'address.buildingName', '')}
                            />
                          </FieldWithLabel>
                          <FieldWithLabel
                            label="Street*"
                            touched={get(touched, 'address.street', false)}
                            error={get(errors, 'address.street', '')}
                          >
                            <Input
                              name="address.street"
                              value={values.address.street}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              touched={get(touched, 'address.street', false)}
                              error={get(errors, 'address.street', '')}
                            />
                          </FieldWithLabel>
                          <FieldWithLabel
                            label="Sub Street"
                            touched={get(touched, 'address.subStreet', false)}
                            error={get(errors, 'address.subStreet', '')}
                          >
                            <Input
                              name="address.subStreet"
                              value={values.address.subStreet}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              touched={get(touched, 'address.subStreet', false)}
                              error={get(errors, 'address.subStreet', '')}
                            />
                          </FieldWithLabel>
                          <FieldWithLabel
                            label="Town"
                            touched={get(touched, 'address.town', false)}
                            error={get(errors, 'address.town', '')}
                          >
                            <Input
                              name="address.town"
                              value={values.address.town}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              touched={get(touched, 'address.town', false)}
                              error={get(errors, 'address.town', '')}
                            />
                          </FieldWithLabel>
                          <FieldWithLabel
                            label="County"
                            touched={get(touched, 'address.county', false)}
                            error={get(errors, 'address.county', '')}
                          >
                            <Input
                              name="address.county"
                              value={values.address.county}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              touched={get(touched, 'address.county', false)}
                              error={get(errors, 'address.county', '')}
                            />
                          </FieldWithLabel>
                          <FieldWithLabel
                            label="Postcode*"
                            touched={get(touched, 'address.postcode', false)}
                            error={get(errors, 'address.postcode', '')}
                          >
                            <Input
                              name="address.postcode"
                              value={values.address.postcode}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              touched={get(touched, 'address.postcode', false)}
                              error={get(errors, 'address.postcode', '')}
                            />
                          </FieldWithLabel>
                          <FieldWithLabel
                            label="Country*"
                            touched={get(touched, 'address.country', false)}
                            error={get(errors, 'address.country', '')}
                          >
                            <Select
                              name="address.country"
                              onChange={(value: string) => setFieldValue('address.country', value)}
                              onBlur={handleBlur}
                              value={countries.toAlpha2(values.address.country) || 0}
                            >
                              {Object.keys(countryList).map((country) => (
                                <option key={country} value={country}>
                                  {countryList[country]}
                                </option>
                              ))}
                            </Select>
                          </FieldWithLabel>
                        </Col>
                      </Row>
                    </Container>
                    <FormActions>
                      <Button buttonType="secondary" type="submit" disabled={isLoading}>
                        Update address
                      </Button>
                    </FormActions>
                  </form>
                );
              }}
            </Formik>
          </RepositCard>
        </Col>
      </Row>
    </Container>
  );
};

export default OrganizationAddress;
